import { type RefObject, useEffect, useRef, useState } from "react";
import {
  ALERT, ANA, ANA_AT, BANNER, BANNER_FOOT, CARD5, FPS, HER, MAIL, NODE, TALL, TALL_CROP, type Word, tallX, tallY, voice,
} from "../../animation/src/notes";
import { TRACK6 } from "../../animation/src/track";

// The loop's two notes, her call written up and the crew's mail, drawn over
// the clean loop by the page rather than burned into it, so that they can be
// kept clear of the nav and the headline on whatever screen this is on. They
// are the film's own (animation/src/Film.tsx), from the same notes.ts, on
// the loop's clock: what the film draws at a frame of a shot, this draws at
// the time the loop shows that frame.

// Where the loop's cuts fall (build-loop.sh, build-loop-tall.sh, beside the
// film on the Desktop). Her shot opens the loop 18 frames in; the cab comes
// up in a 0.75s dissolve at `cab`, its own frame 0 at the dissolve's start,
// and goes in another at `party`. The loop's last dissolve is to her shot's
// first 18 frames, where nothing is drawn.
const DISSOLVE = 0.75;
const WIDE = { cab: 7.9167, party: 12.9583 };
const NARROW = { cab: 6.4167, party: 9.9583 };
const HEAD = 18;

const FILM_W = 1920;
const FILM_H = 1080;

// The film's own cuts, by frame (Reel.tsx's), for the notes over the phone's
// clean copy of it (build-cut-tall.sh): shot 5 from 480, shot 6 from 686, the
// party from 825. Each layer runs on its clip's own clock from the cut.
const FILM_CUT = { shot5: 480, shot6: 686, shot7: 825 };
// That copy is the film cropped to 608x1080, at x 464 for shot 5
// (build-cut.sh), so a point of the film is that much further left in it.
// Shot 6's banner has no leader on a phone, so its crop isn't needed.
const FILM_TALL = { width: 608, height: 1080 };
const FILM_TALL_CROP = { shot5: 464 };
// The phone's card and banner, at the tall loop's own size
const TALL_CARD_W = TALL.width - 48;
// Clear of the nav by this much, and of the screen's sides on a phone
const GAP = 16;

// A turn is grey while it is being said and settles to ink, as the film's do
const LIVE = [120, 113, 108];
const INK = [28, 25, 23];

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const ramp = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const out = (x: number) => 1 - (1 - x) ** 3;
// Remotion's Easing.out(Easing.back(1.3)), the film's pop
const pop = (x: number) => {
  const u = 1 - x;
  return 1 - u * u * (2.3 * u - 1.3);
};
const mix = (a: number[], b: number[], k: number) => `rgb(${a.map((v, i) => Math.round(v + (b[i]! - v) * k)).join(",")})`;

// `clear` is how far down the card may start; `top` and `corner` where a
// phone's banner may, and how much of the right it leaves, for a close button
type Box = { w: number; h: number; clear: number; top: number; corner: number };

/**
 * `loop` is the video the notes are drawn over. `film` puts them on the
 * film's clock and the phone's crop of it rather than the loop's: the phone's
 * copy of the film is clean, as the tall loop is, so these are its notes too.
 */
export function LoopNotes({
  loop,
  tall,
  film = false,
}: {
  loop: RefObject<HTMLVideoElement | null>;
  tall: boolean;
  film?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const guard = useRef<HTMLDivElement>(null);
  const topGuard = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(-1);
  const [box, setBox] = useState<Box | null>(null);
  const [cardH, setCardH] = useState(0);
  const [bannerH, setBannerH] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  // The loop's frame, read every animation frame and kept only when it moves
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = loop.current;
      if (v) setN(Math.floor(v.currentTime * FPS + 0.001));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [loop]);

  // The hero's size, and how far down the nav ends: a guard laid out by the
  // stylesheet, which knows the nav's height and a phone's status bar.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      const clear = guard.current?.offsetTop ?? 80;
      const g = topGuard.current;
      setBox({
        w: el.clientWidth,
        h: el.clientHeight,
        clear,
        top: g?.offsetTop ?? clear,
        corner: g ? el.clientWidth - g.offsetLeft : 0,
      });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  // The card and banner's own heights, unscaled, for the leaders and the
  // clamp under the nav
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (cardRef.current) setCardH(cardRef.current.offsetHeight);
      if (bannerRef.current) setBannerH(bannerRef.current.offsetHeight);
    });
    if (cardRef.current) ro.observe(cardRef.current);
    if (bannerRef.current) ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, []);

  let f5: number, k5: number, f6: number, k6: number, cabLen: number;
  if (film) {
    // The film cuts hard, so each layer is all there for its shot and gone
    // at the next
    f5 = n - FILM_CUT.shot5;
    k5 = n >= FILM_CUT.shot5 && n < FILM_CUT.shot6 ? 1 : 0;
    f6 = n - FILM_CUT.shot6;
    k6 = n >= FILM_CUT.shot6 && n < FILM_CUT.shot7 ? 1 : 0;
    cabLen = FILM_CUT.shot7 - FILM_CUT.shot6;
  } else {
    const t = Math.max(n, 0) / FPS;
    const cut = tall ? NARROW : WIDE;
    f5 = HEAD + Math.max(n, 0);
    k5 = n < 0 ? 0 : 1 - ramp(t, cut.cab, cut.cab + DISSOLVE);
    f6 = Math.round((t - cut.cab) * FPS);
    cabLen = Math.round((cut.party + DISSOLVE - cut.cab) * FPS);
    k6 = t < cut.cab ? 0 : ramp(t, cut.cab, cut.cab + DISSOLVE) * (1 - ramp(t, cut.party, cut.party + DISSOLVE));
  }

  // Where her node is in the tall picture: the reframed loop's mapping, or
  // the film's plain crop
  const node = film
    ? ([NODE.x - FILM_TALL_CROP.shot5, NODE.y] as const)
    : ([tallX(NODE.x, TALL_CROP.shot5), tallY(NODE.y)] as const);

  // The picture's cover crop: where a point of the frame lands on the hero
  const vw = tall ? (film ? FILM_TALL.width : TALL.width) : FILM_W;
  const vh = tall ? (film ? FILM_TALL.height : TALL.height) : FILM_H;
  const s = box ? Math.max(box.w / vw, box.h / vh) : 1;
  const ox = box ? (box.w - vw * s) / 2 : 0;
  let oy = box ? (box.h - vh * s) / 2 : 0;
  // On a phone the card is across the top, so the picture is let down, as
  // far as the crop allows, until her head is clear of it: the crop comes
  // off her feet instead, behind the headline. Where the screen is tall
  // enough, the crop stays centred.
  const cardScale = box ? (box.w - GAP * 2) / TALL_CARD_W : 1;
  if (box && tall && cardH) {
    const below = box.clear + cardH * cardScale + 40 - node[1] * s;
    oy = Math.min(0, Math.max(oy, below));
  }
  const drop = tall ? oy : null;
  useEffect(() => {
    if (loop.current && drop !== null) loop.current.style.objectPosition = `50% ${drop}px`;
  }, [loop, drop]);
  const place = (x: number, y: number) => [ox + x * s, oy + y * s] as const;

  // ── Her card ──
  // The card first, grown and settled; then the node lands beside her and
  // the leader draws out to the card's edge. A leader drawn first runs
  // under the card as it grows, and shows through it.
  const grow = pop(ramp(f5, 20, 38));
  const fade5 = ramp(f5, 20, 28);
  const node5 = pop(ramp(f5, 38, 44));
  const draw5 = out(ramp(f5, 42, 54));
  const pulse5 = (((f5 - 44 + 40) % 40) + 40) % 40 / 40;

  let card: { x: number; y: number; scale: number; w: number; pad: number; origin: string; turn: string } | null = null;
  let lead5: { nx: number; ny: number; ex: number; ey: number; show: boolean } | null = null;
  if (box && k5 > 0) {
    if (tall) {
      // A phone: across the top, under the nav, whatever the screen's height;
      // the leader runs up to it from beside her head.
      const scale = cardScale;
      const [nx, ny] = place(node[0], node[1]);
      const bottom = box.clear + cardH * scale;
      card = { x: GAP, y: box.clear, scale, w: TALL_CARD_W, pad: 24, origin: `${(nx - GAP) / scale}px 100%`, turn: "" };
      // Where the screen is too short for her head to show under the card,
      // the node would be under it, so there is none.
      lead5 = { nx, ny, ex: nx, ey: bottom, show: ny > bottom + 24 };
    } else {
      // Wider: beside her, as in the film, but never under the nav nor off
      // the hero's right edge.
      const scale = Math.max(s, 0.55);
      const [nx, ny] = place(NODE.x, NODE.y);
      let [x, y] = place(CARD5.x, CARD5.y);
      x = Math.min(x, box.w - GAP - CARD5.w * scale);
      y = Math.max(y, box.clear);
      const oyCard = (NODE.y - CARD5.y) * scale;
      card = { x, y, scale, w: CARD5.w, pad: 28, origin: `0px ${NODE.y - CARD5.y}px`, turn: "rotateY(5deg) " };
      lead5 = { nx, ny, ex: x, ey: y + oyCard, show: true };
    }
  }

  // ── The crew's mail ──
  // As with her card: the banner up first, then the node and its leader
  const node6 = pop(ramp(f6, ALERT + 14, ALERT + 20));
  const draw6 = out(ramp(f6, ALERT + 18, ALERT + 28));
  const leave = 1 - ramp(f6, cabLen - 14, cabLen - 2) ** 2;
  const pulse6 = (((f6 - ALERT - 20 + 40) % 40) + 40) % 40 / 40;
  let banner: { x: number; y: number; scale: number; w: number; transform: string; origin: string; opacity: number } | null = null;
  let lead6: { nx: number; ny: number; ex: number; ey: number } | null = null;
  let glow: { x: number; y: number; s: number; opacity: number } | null = null;
  if (box && k6 > 0) {
    if (tall) {
      // A phone: down from the top, as a phone's own banner comes, under the
      // nav, or over the whole screen beside its close button; no leader, the
      // phone on the dash is under the headline.
      const enter = pop(ramp(f6, ALERT, ALERT + 14));
      const scale = (box.w - GAP * 2 - box.corner) / TALL_CARD_W;
      banner = {
        x: GAP, y: box.top, scale, w: TALL_CARD_W, origin: "50% 0%",
        opacity: ramp(f6, ALERT, ALERT + 5) * leave * k6,
        transform: `translateY(${-(1 - enter) * 60 - (1 - leave) * 24}px) scale(${0.94 + 0.06 * enter})`,
      };
    } else {
      // Wider: standing over the phone, as in the film, held by its foot
      // where the leader meets it, and never under the nav.
      const enter = pop(ramp(f6, ALERT, ALERT + 14));
      const scale = Math.max(s, 0.55);
      const [bx, by] = place(BANNER.x, BANNER.y + BANNER_FOOT);
      const foot = Math.max(by, box.clear + bannerH * scale);
      const tr = TRACK6[Math.min(Math.max(f6, 0), TRACK6.length - 1)]!;
      const [nx, ny] = place(tr[0], tr[1]);
      banner = {
        x: bx, y: foot - bannerH * scale, scale, w: BANNER.w, origin: "50% 100%",
        opacity: ramp(f6, ALERT, ALERT + 5) * leave * k6,
        transform: `translateY(${(1 - enter) * 44 - (1 - leave) * 12}px) scale(${0.9 + 0.1 * enter})`,
      };
      lead6 = { nx, ny, ex: bx + (BANNER.w / 2) * scale, ey: foot };
      const [gx, gy] = place(250, 985);
      glow = { x: gx, y: gy, s, opacity: ramp(f6, ALERT, ALERT + 5) * leave * k6 };
    }
  }

  const stroke = Math.max(1.6, 2.5 * s);

  return (
    <div className={`notes${film ? " film" : ""}`} ref={root} aria-hidden="true">
      <div className="guard" ref={guard} />
      <div className="guard top" ref={topGuard} />

      {glow && (
        <div
          className="glow"
          style={{
            left: glow.x - 420 * glow.s, top: glow.y - 150 * glow.s,
            width: 840 * glow.s, height: 300 * glow.s, opacity: glow.opacity,
          }}
        />
      )}

      {box && (
        <svg width={box.w} height={box.h}>
          {lead5 && lead5.show && (
            <g opacity={k5}>
              {f5 >= 44 && <Ring x={lead5.nx} y={lead5.ny} p={pulse5} s={s} />}
              <Leader {...lead5} draw={draw5} w={stroke} />
              <Node x={lead5.nx} y={lead5.ny} k={node5} s={s} w={stroke} />
            </g>
          )}
          {lead6 && (
            <g opacity={leave * k6}>
              {f6 >= ALERT + 20 && <Ring x={lead6.nx} y={lead6.ny} p={pulse6} s={s} />}
              <Leader {...lead6} draw={draw6} w={stroke} />
              <Node x={lead6.nx} y={lead6.ny} k={node6} s={s} w={stroke} />
            </g>
          )}
        </svg>
      )}

      {/* Laid out at the film's own size and scaled to the screen, so the
          words wrap where they do in the film. Kept in the page between
          showings, so their heights are known when they come in. */}
      <div
        className="card"
        style={{
          left: card?.x ?? 0, top: card?.y ?? 0, width: card?.w ?? TALL_CARD_W,
          transform: `scale(${card?.scale ?? 1})`,
          opacity: card ? fade5 * k5 : 0,
          perspective: 1600,
        }}
      >
        <div
          ref={cardRef}
          className="face"
          style={{
            padding: card?.pad ?? 24,
            transform: `${card?.turn ?? ""}scale(${0.4 + 0.6 * grow})`,
            transformOrigin: card?.origin,
          }}
        >
          <Transcript f={f5} />
        </div>
      </div>

      <div
        className="banner"
        style={{
          left: banner?.x ?? 0, top: banner?.y ?? 0, width: banner?.w ?? BANNER.w,
          transform: `scale(${banner?.scale ?? 1})`,
          opacity: banner?.opacity ?? 0,
        }}
      >
        <div ref={bannerRef} className="face" style={{ transform: banner?.transform, transformOrigin: banner?.origin }}>
          <Mail />
        </div>
      </div>
    </div>
  );
}

function Ring({ x, y, p, s }: { x: number; y: number; p: number; s: number }) {
  return <circle cx={x} cy={y} r={(7 + p * 24) * s} fill="none" stroke="#fff" strokeWidth={2 * Math.max(s, 0.7)} opacity={(1 - p) * 0.7} />;
}

function Node({ x, y, k, s, w }: { x: number; y: number; k: number; s: number; w: number }) {
  return (
    <circle className="dot" cx={x} cy={y} r={Math.max(7 * s, 5) * k} fill="#fff" stroke="#fff" strokeWidth={w} opacity={Math.min(1, k * 2)} />
  );
}

function Leader({ nx, ny, ex, ey, draw, w }: { nx: number; ny: number; ex: number; ey: number; draw: number; w: number }) {
  return (
    <line
      className="dot"
      x1={nx} y1={ny} x2={ex} y2={ey}
      stroke="#fff" strokeWidth={w} pathLength={1} strokeDasharray="1 1" strokeDashoffset={1 - draw} opacity={0.95}
    />
  );
}

// The live sign: a run of grey bars, moving with whoever is talking
function Waveform({ f, t }: { f: number; t: number }) {
  const n = 11;
  const v = voice(t);
  return (
    <div className="wave">
      {Array.from({ length: n }, (_, i) => {
        const centre = 1 - Math.abs(i - (n - 1) / 2) / ((n - 1) / 2 + 1);
        const wobble = 0.5 + 0.5 * Math.sin(f * 0.95 + i * 1.7) * Math.cos(f * 0.41 + i * 0.6);
        return <i key={i} style={{ height: 3 + v * (2 + 11 * centre * wobble) }} />;
      })}
    </div>
  );
}

// One speaker's turn: the words as they are said, grey while the turn is
// live, settling to ink once it is over
function Turn({ label, words, t }: { label: string; words: Word[]; t: number }) {
  const first = words[0]![1];
  const last = words[words.length - 1]![2];
  // Laid out from the start, so the card is its full height before the turn
  // shows and the picture can be placed under it once
  return (
    <div className="said" style={{ visibility: t < first - 0.25 ? "hidden" : undefined }}>
      <small>{label}</small>
      <p style={{ color: mix(LIVE, INK, ramp(t, last + 0.3, last + 0.9)) }}>
        {words.map(([w, a], i) => (
          <span key={i} style={{ opacity: ramp(t, a, a + 0.12) }}>{w} </span>
        ))}
      </p>
    </div>
  );
}

function Transcript({ f }: { f: number }) {
  const t = f / FPS;
  // The call's own clock: it has been going since Ana picked up
  const call = t - ANA_AT;
  const clock = `${Math.floor(call / 60)}:${String(Math.floor(call % 60)).padStart(2, "0")}`;
  return (
    <>
      <div className="head">
        <i className="orb" data-tone="receptionist" />
        <b>Receptionist</b>
        <span>
          <Waveform f={f} t={t} />
          {clock}
        </span>
      </div>
      <Turn label="Receptionist" words={ANA} t={t} />
      <Turn label="Caller" words={HER} t={t} />
    </>
  );
}

// The banner as iOS draws the crew's mail arriving: Gmail's icon on its
// white tile
function Mail() {
  return (
    <>
      <div className="app">
        <img src="/logos/gmail.svg" alt="" />
      </div>
      <div className="msg">
        <div>
          <b>{MAIL.from}</b>
          <span>now</span>
        </div>
        <p>{MAIL.subject}</p>
        <p>{MAIL.short}</p>
      </div>
    </>
  );
}
